/**
 * A `#t=` media-fragment URL, which makes a `<video preload="metadata">` seek to
 * that moment and PAINT the frame — giving it a poster without one.
 *
 * This is the cheap path, and the only one worth taking when a page shows many
 * clips at once: the browser fetches the container header and one frame's worth
 * of data, nothing more. `captureVideoThumbnails` below is the expensive path —
 * it sets `preload="auto"`, so it downloads each video IN FULL to draw onto a
 * canvas. That is right for the upload picker, where a person chose one file and
 * a real JPEG has to come out of it; it would be ruinous for a 15-shot brief.
 *
 * Not 0: seeking to exactly the first frame lands on black on plenty of clips
 * that fade in, and some encoders have no keyframe there.
 *
 * The fragment is client-side only — never sent to the server — so it is safe to
 * append to a presigned URL whose signature covers the query string.
 */
export function posterFrameUrl(videoUrl: string, atSeconds = 0.5): string {
  const url = videoUrl.trim();
  if (!url) return url;
  // Replace an existing fragment rather than appending a second one.
  const base = url.split("#")[0];
  return `${base}#t=${atSeconds}`;
}

export interface ThumbnailCandidate {
  id: string;
  blob: Blob;
  /** Object URL for the <img> preview. The caller revokes it. */
  previewUrl: string;
  atSeconds: number;
}

/** Longest edge of a captured frame. 1280 keeps a 4K source under ~250 KB of JPEG. */
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;
const SEEK_TIMEOUT_MS = 8000;

function waitForEvent(el: HTMLVideoElement, event: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      el.removeEventListener(event, onEvent);
      reject(new Error(`timeout waiting for ${event}`));
    }, timeoutMs);
    const onEvent = () => {
      window.clearTimeout(timer);
      resolve();
    };
    el.addEventListener(event, onEvent, { once: true });
  });
}

function seekTime(duration: number, fraction: number): number {
  const lo = 0.05;
  const hi = duration - 0.1;
  const raw = duration * fraction;
  if (hi <= lo) return Math.max(0, Math.min(raw, Math.max(0, duration - 0.01)));
  return Math.min(hi, Math.max(lo, raw));
}

async function frameToBlob(video: HTMLVideoElement): Promise<Blob | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (w <= 0 || h <= 0) return null;
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
  });
}

/**
 * Capture JPEG frames from a local File or a remote (CORS-open) URL.
 * Undecodable codecs resolve to `[]` rather than throwing — the picker treats
 * that as "upload your own".
 */
export async function captureVideoThumbnails(
  source: File | string,
  fractions: number[] = [0.05, 0.5, 0.9],
): Promise<ThumbnailCandidate[]> {
  let objectUrl: string | null = null;
  let video: HTMLVideoElement | null = null;

  try {
    video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.setAttribute("playsinline", "true");
    video.style.position = "fixed";
    video.style.left = "-9999px";
    video.style.width = "1px";
    video.style.height = "1px";
    document.body.appendChild(video);

    if (typeof source === "string") {
      // crossOrigin must land before src or the canvas is tainted and toBlob throws.
      video.crossOrigin = "anonymous";
      video.src = source;
    } else {
      objectUrl = URL.createObjectURL(source);
      video.src = objectUrl;
    }

    await waitForEvent(video, "loadeddata", SEEK_TIMEOUT_MS);

    // iOS Safari can draw a blank frame until the element has actually played.
    await video.play().catch(() => {});
    video.pause();

    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      video.currentTime = 1e6;
      await waitForEvent(video, "durationchange", SEEK_TIMEOUT_MS).catch(() => {});
      video.currentTime = 0;
      await waitForEvent(video, "seeked", SEEK_TIMEOUT_MS).catch(() => {});
    }

    const duration = video.duration;
    const times: number[] = [];
    if (!Number.isFinite(duration) || duration <= 0) {
      times.push(0);
    } else {
      for (const fraction of fractions) {
        const t = seekTime(duration, fraction);
        if (!times.some((existing) => Math.abs(existing - t) < 0.05)) times.push(t);
      }
    }

    const candidates: ThumbnailCandidate[] = [];
    for (const atSeconds of times) {
      video.currentTime = atSeconds;
      try {
        await Promise.race([
          waitForEvent(video, "seeked", SEEK_TIMEOUT_MS),
          new Promise<never>((_, reject) => {
            window.setTimeout(() => reject(new Error("seek timeout")), SEEK_TIMEOUT_MS);
          }),
        ]);
      } catch {
        continue;
      }
      const blob = await frameToBlob(video);
      if (!blob) continue;
      candidates.push({
        id: `frame-${atSeconds.toFixed(2)}`,
        blob,
        previewUrl: URL.createObjectURL(blob),
        atSeconds,
      });
    }
    return candidates;
  } catch {
    return [];
  } finally {
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
      video.remove();
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
